import {
  DocumentTextIcon,
  PencilIcon,
  EyeIcon,
  PhotoIcon,
  VideoCameraIcon,
  CogIcon,
  PlayIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

export default function DocumentBuilderHelp() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center mb-6">
          <DocumentTextIcon className="h-8 w-8 text-indigo-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Document Builder</h1>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            The Document Builder is a powerful tool for creating professional training materials with customizable templates,
            multimedia content, and interactive elements. Build comprehensive training courses with multiple sections and pages.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Key Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <PencilIcon className="h-6 w-6 text-indigo-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Template Selection</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Choose from multiple professional templates including headers, feature sections, content blocks, and video sections.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <PhotoIcon className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Visual Customization</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Customize colors, fonts, backgrounds, and images to match your brand and training objectives.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <VideoCameraIcon className="h-6 w-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Multimedia Integration</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Embed YouTube videos and other media content to create engaging, interactive training experiences.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <CogIcon className="h-6 w-6 text-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">AI Assistance</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Use AI-powered content generation and suggestions to accelerate document creation.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Available Templates</h2>

          <div className="space-y-4 mb-8">
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Header Section</h4>
              <p className="text-gray-600 text-sm mb-2">Create impactful opening sections with large headings and background images.</p>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <div className="inline-block px-4 py-2 rounded-lg shadow-md bg-gradient-to-r from-white/5 to-white/20 backdrop-blur-sm">
                    <h2 className="text-2xl font-semibold text-indigo-600 opacity-75 cursor-not-allowed">Your Training Title</h2>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Feature Section</h4>
              <p className="text-gray-600 text-sm mb-2">Highlight key features with side-by-side content and images.</p>
              <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-200 h-32 rounded-lg flex items-center justify-center">
                    <PhotoIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-600 opacity-75 cursor-not-allowed mb-2">Feature Title</h3>
                    <p className="text-gray-500 opacity-75 cursor-not-allowed text-sm">Feature description and benefits explained here.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">3 Paragraph Section</h4>
              <p className="text-gray-600 text-sm mb-2">Structure content with three distinct paragraphs and visual elements.</p>
              <div className="bg-gradient-to-r from-indigo-50 via-blue-50 to-green-50 rounded-lg p-4 border-4 border-dashed" style={{borderImage: "linear-gradient(to right, #6366F1 10%, #0EA5E9 30%, #10B981 90%) 1", borderImageSlice: 1}}>
                <div className="space-y-3">
                  <div className="border p-4 rounded-lg bg-white shadow-sm">
                    <p className="text-gray-600 opacity-75 cursor-not-allowed">First paragraph content...</p>
                  </div>
                  <div className="border p-4 rounded-lg bg-white shadow-sm">
                    <p className="text-gray-600 opacity-75 cursor-not-allowed">Second paragraph content...</p>
                  </div>
                  <div className="border p-4 rounded-lg bg-white shadow-sm">
                    <p className="text-gray-600 opacity-75 cursor-not-allowed">Third paragraph content...</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Video Section</h4>
              <p className="text-gray-600 text-sm mb-2">Embed videos for interactive learning experiences.</p>
              <div className="bg-black rounded-lg p-4 border-2 border-dashed border-gray-300">
                <div className="bg-gray-800 h-32 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <VideoCameraIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm opacity-75 cursor-not-allowed">YouTube Video Player</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Use</h2>

          <div className="space-y-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-indigo-600 font-semibold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Select a Template</h4>
                <p className="text-gray-600 text-sm">Choose from available templates or start with a blank document.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-indigo-600 font-semibold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Customize Content</h4>
                <p className="text-gray-600 text-sm">Edit headings, paragraphs, and other text content for your training material.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-indigo-600 font-semibold text-sm">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Visual Styling</h4>
                <p className="text-gray-600 text-sm">Adjust colors, fonts, backgrounds, and add images to enhance visual appeal.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-indigo-600 font-semibold text-sm">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Add Multimedia</h4>
                <p className="text-gray-600 text-sm">Embed videos, add images, and include interactive elements.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-indigo-600 font-semibold text-sm">5</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Preview & Save</h4>
                <p className="text-gray-600 text-sm">Preview your document and save it to make it available for training courses.</p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best Practices</h2>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <CheckCircleIcon className="h-6 w-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Content Organization</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• Use clear, descriptive headings for each section</li>
                  <li>• Break complex topics into digestible paragraphs</li>
                  <li>• Include visual elements to support text content</li>
                  <li>• Maintain consistent styling throughout the document</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-green-900 mb-2">Engagement Tips</h4>
                <ul className="text-green-800 text-sm space-y-1">
                  <li>• Incorporate relevant images and videos to enhance learning</li>
                  <li>• Use brand colors and fonts for professional appearance</li>
                  <li>• Include interactive elements when possible</li>
                  <li>• Test documents on different devices for responsiveness</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <ArrowPathIcon className="h-6 w-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-2">AI Assistance</h4>
                <p className="text-yellow-800 text-sm mb-2">
                  Leverage AI features to generate content suggestions and improve document quality.
                </p>
                <div className="bg-white rounded-lg p-4 border border-yellow-300">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">AI Content Generation</span>
                    <button className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs opacity-75 cursor-not-allowed">
                      Generate
                    </button>
                  </div>
                  <textarea
                    className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                    placeholder="Enter your content requirements..."
                    rows="2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}